
import React from "react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Textarea } from "@/components/ui/textarea";
import { TriStateBooleanToggle } from "@/components/ui/tri-state-boolean-toggle";
import { asTriState } from "@/lib/triState";
import { ApplyToAllButtonWithConfirm } from "../components/ApplyToAllButtonWithConfirm";

export type AdditionalInfoApplyToAllValue = {
  doesWaitlist: boolean | null;
  doesScholarships: boolean | null;
  scholarshipNotes: string;
};

export const AdditionalInfoSection = ({
  form,
  onApplyAdditionalInfoToAll,
  applyToAllLoading = false,
}: {
  form: any;
  onApplyAdditionalInfoToAll?: (value: AdditionalInfoApplyToAllValue) => Promise<void>;
  applyToAllLoading?: boolean;
}) => (
  <Card>
    <CardHeader>
      <div className="flex items-center justify-between gap-3">
        <CardTitle>Additional Information</CardTitle>
        {onApplyAdditionalInfoToAll && (
          <ApplyToAllButtonWithConfirm
            disabled={applyToAllLoading}
            dialogTitle="Apply to all locations?"
            dialogDescription="This will overwrite Does Waitlist, Does Scholarships, and Scholarship Notes for every location that shares the same camp organization. This updates drafts and cannot be undone."
            onConfirm={async () => {
              const doesWaitlist = asTriState(form?.getValues?.("doesWaitlist"));
              const doesScholarships = asTriState(form?.getValues?.("doesScholarships"));
              const scholarshipNotes = String(form?.getValues?.("scholarshipNotes") ?? "");
              await onApplyAdditionalInfoToAll({ doesWaitlist, doesScholarships, scholarshipNotes });
            }}
          />
        )}
      </div>
    </CardHeader>
    <CardContent className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormField
          control={form.control}
          name="doesWaitlist"
          render={({ field }: any) => (
            <FormItem className="flex flex-col rounded-lg border p-4 items-start">
              <div className="space-y-0.5 mb-2">
                <FormLabel className="text-base text-left w-full">Does Waitlist</FormLabel>
                <FormDescription>
                  Does this location typically use a waitlist when full?
                </FormDescription>
              </div>
              <FormControl>
                <TriStateBooleanToggle value={field.value} onChange={field.onChange} aria-label="Does Waitlist" />
              </FormControl>
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="doesScholarships"
          render={({ field }: any) => (
            <FormItem className="flex flex-col rounded-lg border p-4 items-start">
              <div className="space-y-0.5 mb-2">
                <FormLabel className="text-base text-left w-full">Does Scholarships</FormLabel>
                <FormDescription>
                  Does this location offer scholarships?
                </FormDescription>
              </div>
              <FormControl>
                <TriStateBooleanToggle value={field.value} onChange={field.onChange} aria-label="Does Scholarships" />
              </FormControl>
            </FormItem>
          )}
        />
      </div>

      {form.watch("doesScholarships") === true && (
        <FormField
          control={form.control}
          name="scholarshipNotes"
          render={({ field }: any) => (
            <FormItem>
              <FormLabel className="text-left block">Scholarship Notes</FormLabel>
              <FormControl>
                <Textarea {...field} />
              </FormControl>
              <FormDescription>Additional details about scholarships</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
      )}
    </CardContent>
  </Card>
);
